import { Clock, Calculator, AlertTriangle, TrendingDown, Users, FileText, History, Lightbulb } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  ProblemBlock,
  BenefitCard,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const calculations = [
  {
    icon: Users,
    title: "Coût main d'œuvre supplémentaire",
    description: "Chaque jour de retard, c'est des salaires en plus à payer.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: FileText,
    title: "Pénalités contractuelles",
    description: "Calcul automatique selon les clauses de votre contrat.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
  {
    icon: TrendingDown,
    title: "Impact sur la marge finale",
    description: "Visualisez comment le retard affecte votre rentabilité.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: AlertTriangle,
    title: "Nouveau Jour Critique",
    description: "Recalcul du jour critique avec le retard simulé.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const features = [
  {
    icon: Calculator,
    title: "Simulateur interactif",
    description: "\"Et si le chantier prend X jours de retard ?\" Ajustez et visualisez l'impact instantanément.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: FileText,
    title: "Pénalités automatiques",
    description: "Configurez les pénalités contractuelles et laissez B8ild les calculer.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
  {
    icon: Lightbulb,
    title: "Suggestions de rattrapage",
    description: "B8ild vous propose des actions pour minimiser l'impact du retard.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: History,
    title: "Historique des retards",
    description: "Gardez une trace de tous les retards par chantier pour mieux prévoir.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const RetardsFeature = () => {
  usePageMeta({ title: "Gestion retards chantier BTP — Simulateur B8ild", description: "Simulez l'impact financier des retards sur vos chantiers. Anticipez les pénalités et protégez vos marges avec B8ild.", url: "https://b8ild.fr/product/retards" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Gestion retards chantier BTP — Simulateur B8ild",
        description: "Simulez l'impact financier des retards sur vos chantiers. Anticipez les pénalités et protégez vos marges avec B8ild.",
        url: "https://b8ild.fr/product/retards",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Gestion des retards", item: "https://b8ild.fr/product/retards" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Simulation des retards" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Simulation des retards"
        title="Un retard de 5 jours peut"
        titleHighlight="effacer toute votre marge."
        subtitle="Simulez l'impact financier des retards sur vos chantiers avant de les subir. Prenez les bonnes décisions à temps."
        icon={Clock}
        gradient="from-[#F59E0B] to-[#D97706]"
      />

      {/* Problem */}
      <FeatureSection variant="dark">
        <ProblemBlock
          text="Dans le BTP, les retards sont fréquents : intempéries, livraisons, sous-traitants... Mais chaque jour de retard a un coût. Sans outil, vous le découvrez trop tard."
        />
      </FeatureSection>

      {/* What B8ild calculates */}
      <FeatureSection
        title="Ce que B8ild calcule pour vous"
        subtitle="L'impact complet d'un retard, en un clic"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {calculations.map((calc, index) => (
            <BenefitCard key={index} {...calc} />
          ))}
        </div>
      </FeatureSection>

      {/* Simulator Preview */}
      <FeatureSection variant="dark" title="Simulateur interactif">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Simulation de retard</h3>
                <span className="text-sm text-white/40">Chantier Villa Durand</span>
              </div>

              <div className="p-6">
                {/* Slider simulation */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-white/50">Retard simulé</span>
                    <span className="text-2xl font-bold text-[#F59E0B]">5 jours</span>
                  </div>
                  <div className="relative h-3 bg-white/[0.05] rounded-full">
                    <div className="absolute left-0 top-0 h-full w-[25%] bg-gradient-to-r from-[#10B981] to-[#F59E0B] rounded-full" />
                    <div className="absolute top-1/2 left-[25%] -translate-y-1/2 -translate-x-1/2 h-5 w-5 bg-[#F59E0B] rounded-full shadow-lg" />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-white/30">
                    <span>0 jour</span>
                    <span>20 jours</span>
                  </div>
                </div>

                {/* Results */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Before */}
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4">
                    <h4 className="text-sm font-medium text-white/50 mb-4">Sans retard</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Marge finale</span>
                        <span className="font-semibold text-[#10B981]">+8 500 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Taux de marge</span>
                        <span className="font-semibold text-white">22%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Jour critique</span>
                        <span className="font-semibold text-white">J+15</span>
                      </div>
                    </div>
                  </div>

                  {/* After */}
                  <div className="rounded-xl bg-[#EF4444]/5 border border-[#EF4444]/20 p-4">
                    <h4 className="text-sm font-medium text-[#EF4444]/70 mb-4">Avec 5 jours de retard</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Marge finale</span>
                        <span className="font-semibold text-[#EF4444]">+5 000 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Taux de marge</span>
                        <span className="font-semibold text-[#F59E0B]">13%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Jour critique</span>
                        <span className="font-semibold text-[#EF4444]">J+10</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Impact summary */}
                <div className="mt-6 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/20 p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />
                    <div>
                      <p className="text-white font-medium">Impact du retard : -3 500 €</p>
                      <p className="text-sm text-white/50">
                        Coût MO : -2 800 € • Pénalités : -700 €
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Features */}
      <FeatureSection
        title="Fonctionnalités complètes"
        subtitle="Tout ce qu'il faut pour anticiper les retards"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <BenefitCard key={index} {...feature} />
          ))}
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              5 jours de retard, 4 000€ en fumée
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Un macon fait un chantier d'extension a 80 000 EUR HT. Duree prevue : 60 jours. Marge previsionnelle : 14 000 EUR, soit 17,5%. Le chantier prend 5 jours de retard a cause d'un sous-traitant beton.
              </p>
              <p>
                Cout du retard : 4 ouvriers x 180 EUR/jour x 5 jours = 3 600 EUR de main d'oeuvre improductive. Plus 400 EUR de location de materiel supplementaire. Total : 4 000 EUR de surcout. La marge fond a 10 000 EUR, soit 12,5%.
              </p>
              <p>
                Sur un marche prive, le client peut appliquer des penalites de retard : 1/1000e du montant HT par jour, soit 80 EUR/jour. Plafond : 5%, soit 4 000 EUR. Le retard coute double : la penalite + le surcout reel. Sur 10 chantiers par an, 2-3 retards de ce type effacent 10 000 a 15 000 EUR de benefice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment calculer les penalites de retard ?",
                  a: "En marche prive : 1/1000e du montant HT par jour calendaire, plafonnees a 5%. Exemple : chantier 50 000 EUR, 10 jours de retard = 500 EUR de penalite. En marche public : 1/3000e HT par jour, plafond 10%. En CCMI : 1/3000e du prix par jour. Les penalites ne courent qu'apres mise en demeure ecrite, sauf clause contraire.",
                },
                {
                  q: "Comment prevenir les retards sur un chantier ?",
                  a: "Planifiez les approvisionnements 2 semaines avant le besoin reel. Identifiez le chemin critique : la sequence de taches qui ne peut pas prendre de retard. Suivez l'avancement reel vs prevu chaque semaine. B8ild detecte les derives de planning et alerte avant que le retard ne s'accumule.",
                },
                {
                  q: "Quelles sont les causes de retard les plus frequentes ?",
                  a: "Les 3 premieres : retard de livraison materiaux (35% des cas), intemperies (25%), et sous-estimation du temps de travail (20%). Les solutions : commander les materiaux critiques des la signature du devis, prevoir 10% de marge de temps pour la meteo, et chronometrer les taches recurrentes pour affiner vos estimations.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Anticipez l'impact des retards"
        subtitle="Ne subissez plus les retards, prévoyez-les."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="retards" />

      <LandingFooter />
    </div>
  );
};

export default RetardsFeature;
